import type { ToolCall } from '@kanbu/schema';
import {
  type CompareShopProductsArtifact,
  type GetShopCategoriesArtifact,
  type GetShopDeliveryOptionsArtifact,
  type GetShopProductAccessoriesArtifact,
  type GetShopProductArtifact,
  type GetShopProductAvailabilityArtifact,
  type GetShopProductGiftsArtifact,
  type GetShopProductsArgs,
  type GetShopProductsArtifact,
  type ShopCategoryPreview,
  type ShopProductPreview,
  ToolNames,
} from '@kanbu/schema/contracts';
import { ProductAvailabilityStatus } from '@kanbu/schema/enums';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import {
  ArrowUpDown,
  BadgeCent,
  Gift,
  PackageCheck,
  Search,
  Tag,
  Truck,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useCallback } from 'react';

import {
  DetailChip,
  DetailLinkChip,
  DetailRow,
  formatPrice,
} from './detail-primitives';

const PRODUCT_LOOKUP_TOOLS: ReadonlySet<string> = new Set([
  ToolNames.GetShopProductDetails,
  /** Retired lookups — kept so persisted conversations still render. */
  ToolNames.GetShopProductByProductId,
  ToolNames.GetShopProductByExternalId,
  ToolNames.GetShopProductByEan,
  ToolNames.GetShopProductByIsbn,
]);

/** One compact product line: small thumbnail + title + price, linking out. */
function ProductLine({ product }: { product: ShopProductPreview }) {
  const price = formatPrice(product.price, product.currency);

  return (
    <a
      href={product.url}
      target='_blank'
      rel='noopener noreferrer'
      className='flex max-w-full items-center gap-2 rounded-lg border border-chat-border bg-chat-background-secondary px-2 py-1 hover:bg-chat-surface'
    >
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt=''
          className='size-8 shrink-0 rounded object-cover'
        />
      ) : null}
      <span className='min-w-0 flex-1 truncate text-chat-foreground'>
        {product.title || product.name}
      </span>
      {price ? (
        <span className='shrink-0 font-semibold text-chat-foreground'>
          {price}
        </span>
      ) : null}
    </a>
  );
}

function ProductLookupDetail({ toolCall }: { toolCall: ToolCall }) {
  const product = toolCall.artifact as GetShopProductArtifact | undefined;

  if (!product?.name) {
    return null;
  }

  return (
    <div className='pl-6'>
      <ProductLine
        product={{
          id: product.id,
          name: product.name,
          title: product.title,
          price: product.price,
          currency: product.currency,
          imageUrl: product.imageUrl,
          url: product.url,
        }}
      />
    </div>
  );
}

function ProductsDetail({ toolCall }: { toolCall: ToolCall }) {
  const { t } = useLingui();
  const args = toolCall.args as GetShopProductsArgs;
  const artifact = toolCall.artifact as GetShopProductsArtifact | undefined;

  if (!artifact) {
    return null;
  }

  const sortLabel = getSortLabel(t, args);
  const hasFacets = Boolean(
    args.query ||
      sortLabel ||
      args.minPrice ||
      args.maxPrice ||
      args.isOnSale ||
      artifact.categories?.length,
  );

  // The found count now lives in the step header; without any query/filter
  // there is nothing left to show in the detail row.
  if (!hasFacets) {
    return null;
  }

  return (
    <DetailRow>
      {args.query ? (
        <DetailChip icon={<Search />}>{t`"${args.query}"`}</DetailChip>
      ) : null}
      {sortLabel ? (
        <DetailChip icon={<ArrowUpDown />}>{sortLabel}</DetailChip>
      ) : null}
      {args.minPrice ? (
        <DetailChip icon={<Wallet />}>{t`from ${args.minPrice} Kč`}</DetailChip>
      ) : null}
      {args.maxPrice ? (
        <DetailChip icon={<Wallet />}>{t`to ${args.maxPrice} Kč`}</DetailChip>
      ) : null}
      {args.isOnSale ? (
        <DetailChip icon={<BadgeCent />}>{t`On sale`}</DetailChip>
      ) : null}
      {artifact.categories?.map(category => (
        <DetailChip key={category.id} icon={<Tag />}>
          {category.displayName ?? category.name}
        </DetailChip>
      ))}
    </DetailRow>
  );
}

function getSortLabel(
  t: ReturnType<typeof useLingui>['t'],
  args: GetShopProductsArgs,
): string | null {
  if (args.sortBy === 'price') {
    return args.sortOrder === 'desc' ? t`Highest price` : t`Lowest price`;
  }

  if (args.sortBy === 'name') {
    return args.sortOrder === 'desc' ? t`Name (Z-A)` : t`Name (A-Z)`;
  }

  return null;
}

/**
 * The categories a browse call surfaced. The tool returns top-level categories
 * each with a `children` preview; the meaningful, actionable set is the
 * subcategories (matching the legacy card). Falls back to the top-level list
 * when a level carries no children. The trail count and the rendered chips both
 * derive from this, so they never disagree.
 */
function flattenCategories(
  artifact: GetShopCategoriesArtifact | undefined,
): ShopCategoryPreview[] {
  const categories = artifact?.categories ?? [];
  const children = categories.flatMap(category => category.children ?? []);

  return children.length > 0 ? children : categories;
}

function CategoriesDetail({ toolCall }: { toolCall: ToolCall }) {
  const categories = flattenCategories(
    toolCall.artifact as GetShopCategoriesArtifact | undefined,
  );

  if (categories.length === 0) {
    return null;
  }

  return (
    <DetailRow>
      {categories.slice(0, 12).map(category => (
        <DetailChip key={category.id} icon={<Tag />}>
          {category.displayName ?? category.name}
        </DetailChip>
      ))}
    </DetailRow>
  );
}

function AccessoriesDetail({ toolCall }: { toolCall: ToolCall }) {
  const artifact = toolCall.artifact as
    | GetShopProductAccessoriesArtifact
    | undefined;
  const accessories = artifact?.accessories ?? [];

  if (accessories.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-1 pl-6'>
      {accessories.slice(0, 5).map(product => (
        <ProductLine key={product.id} product={product} />
      ))}
    </div>
  );
}

function DeliveryDetail({ toolCall }: { toolCall: ToolCall }) {
  const { t } = useLingui();
  const artifact = toolCall.artifact as
    | GetShopDeliveryOptionsArtifact
    | undefined;
  const options = artifact?.deliveryOptions ?? [];

  if (options.length === 0) {
    return null;
  }

  return (
    <DetailRow>
      {options.map((option, index) => {
        const price = formatPrice(option.price);

        return (
          <DetailChip key={option.carrierId ?? index} icon={<Truck />}>
            {option.carrier ?? t`Carrier`}
            {price ? ` · ${price}` : ''}
          </DetailChip>
        );
      })}
    </DetailRow>
  );
}

function availabilityLabel(
  t: ReturnType<typeof useLingui>['t'],
  status: ProductAvailabilityStatus | null | undefined,
): string {
  switch (status) {
    case ProductAvailabilityStatus.InStock:
      return t`In stock`;
    case ProductAvailabilityStatus.OutOfStock:
      return t`Out of stock`;
    case ProductAvailabilityStatus.OnOrder:
      return t`On order`;
    case ProductAvailabilityStatus.PreOrder:
      return t`Pre-order`;

    default:
      return t`Availability unknown`;
  }
}

function AvailabilityDetail({ toolCall }: { toolCall: ToolCall }) {
  const { t } = useLingui();
  const artifact = toolCall.artifact as
    | GetShopProductAvailabilityArtifact
    | undefined;

  if (!artifact) {
    return null;
  }

  const hasStock =
    artifact.stockQuantity !== null && artifact.stockQuantity !== undefined;

  return (
    <DetailRow>
      <DetailChip icon={<PackageCheck />}>
        {availabilityLabel(t, artifact.availability)}
        {hasStock ? ` · ${artifact.stockQuantity} ${t`pcs`}` : ''}
      </DetailChip>
    </DetailRow>
  );
}

function GiftsDetail({ toolCall }: { toolCall: ToolCall }) {
  const artifact = toolCall.artifact as GetShopProductGiftsArtifact | undefined;
  const gifts = artifact?.gifts ?? [];

  if (gifts.length === 0) {
    return null;
  }

  return (
    <DetailRow>
      {gifts.map(gift => (
        <DetailChip key={gift} icon={<Gift />}>
          {gift}
        </DetailChip>
      ))}
    </DetailRow>
  );
}

function CompareDetail({ toolCall }: { toolCall: ToolCall }) {
  const artifact = toolCall.artifact as CompareShopProductsArtifact | undefined;
  const products = artifact?.products ?? [];

  if (products.length === 0) {
    return null;
  }

  return (
    <DetailRow>
      {products.map(product => (
        <DetailLinkChip key={product.id} href={product.url}>
          {product.title || product.name}
        </DetailLinkChip>
      ))}
    </DetailRow>
  );
}

/**
 * Compact, customer-safe detail for shop research tools — surfaces the useful
 * result facets (found count + query/filters, looked-up product, delivery
 * options, stock, gifts, compared products) without the heavy presentation
 * cards those tools' registry components render. Returns `null` for any tool
 * it does not handle.
 */
export function ShopToolDetail({
  toolCall,
}: {
  toolCall: ToolCall;
}): ReactNode {
  switch (toolCall.name) {
    case ToolNames.GetShopProducts:
      return <ProductsDetail toolCall={toolCall} />;
    case ToolNames.GetShopCategories:
      return <CategoriesDetail toolCall={toolCall} />;
    case ToolNames.GetShopProductAccessories:
      return <AccessoriesDetail toolCall={toolCall} />;
    case ToolNames.GetShopDeliveryOptions:
      return <DeliveryDetail toolCall={toolCall} />;
    case ToolNames.GetShopProductAvailability:
      return <AvailabilityDetail toolCall={toolCall} />;
    case ToolNames.GetShopProductGifts:
      return <GiftsDetail toolCall={toolCall} />;
    case ToolNames.CompareShopProducts:
      return <CompareDetail toolCall={toolCall} />;

    default:
      return PRODUCT_LOOKUP_TOOLS.has(toolCall.name) ? (
        <ProductLookupDetail toolCall={toolCall} />
      ) : null;
  }
}

function itemCount(items: unknown): number {
  return Array.isArray(items) ? items.length : 0;
}

/**
 * Returns a short, right-aligned summary of a tool's *result* (a found count
 * or a status) for the step header — kept distinct from the query/filter chips
 * in the expanded detail, which describe the call's inputs. Returns `null` when
 * there is no meaningful count to show.
 */
export function useToolResultSummary(): (toolCall: ToolCall) => string | null {
  const { t } = useLingui();

  return useCallback(
    (toolCall: ToolCall): string | null => {
      const artifact = toolCall.artifact as Record<string, unknown> | undefined;

      if (!artifact) {
        return null;
      }

      switch (toolCall.name) {
        case ToolNames.GetShopProducts: {
          const products = artifact as GetShopProductsArtifact;
          const count = products?.totalCount ?? itemCount(products?.products);

          // `#+` renders as "24+" — the number placeholder followed by a literal
          // "+", so hasMore reads as "more matched beyond these" inline.
          return products?.hasMore
            ? plural(count, {
                one: 'Found #+ product',
                other: 'Found #+ products',
              })
            : plural(count, {
                one: 'Found # product',
                other: 'Found # products',
              });
        }
        case ToolNames.GetShopCategories:
          return plural(
            flattenCategories(artifact as GetShopCategoriesArtifact).length,
            { one: 'Found # category', other: 'Found # categories' },
          );
        case ToolNames.GetShopProductAccessories: {
          const count = itemCount(
            (artifact as GetShopProductAccessoriesArtifact).accessories,
          );

          return plural(count, {
            one: 'Found # accessory',
            other: 'Found # accessories',
          });
        }
        case ToolNames.GetShopProductGifts: {
          const count = itemCount(
            (artifact as GetShopProductGiftsArtifact).gifts,
          );

          return count > 0
            ? plural(count, { one: '# gift', other: '# gifts' })
            : null;
        }
        case ToolNames.GetShopDeliveryOptions: {
          const count = itemCount(
            (artifact as GetShopDeliveryOptionsArtifact).deliveryOptions,
          );

          return count > 0
            ? plural(count, { one: '# option', other: '# options' })
            : null;
        }
        case ToolNames.GetShopProductAvailability:
          return availabilityLabel(
            t,
            (artifact as GetShopProductAvailabilityArtifact).availability,
          );
        case ToolNames.CompareShopProducts:
          return plural(
            itemCount((artifact as CompareShopProductsArtifact).products),
            { one: '# product', other: '# products' },
          );

        default:
          return null;
      }
    },
    [t],
  );
}
